/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import { useFavoritePrayerAction } from "@packages/kids";
import { useAddToQueue } from "@packages/media";
import { useBranch, useRequestPrayer } from "@packages/sdk";
import type { QuickActionButtonProps, ThemeType } from "@packages/ui";
import {
  HideFromKids,
  PlayLastIcon,
  PlayNextIcon,
  QuickAction,
  ShareIcon,
  ShareModal,
  spacing,
  StarIcon,
  useModal,
} from "@packages/ui";
import type { ComponentPropsWithRef } from "react";

const quickActionsContainerStyles: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "center",
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};

type PrayerQuickActionsProps = ComponentPropsWithRef<"div"> & {
  prayerId: number;
  variant?: QuickActionButtonProps["variant"];
  css?: Interpolation<ThemeType>;
};

export const PrayerQuickActions = ({
  prayerId,
  variant = "transparentPrimary",
  css: cssProp,
  ...props
}: PrayerQuickActionsProps) => {
  const t = useTranslations();
  const shareModal = useModal(ShareModal);
  const branch = useBranch();

  const { query: queryPrayer } = useRequestPrayer({
    id: prayerId,
    retryOnMount: false,
    retry: 0,
  });
  const updateFavorite = useFavoritePrayerAction({
    id: prayerId,
    has_access: queryPrayer?.data?.has_access ?? true,
    title: queryPrayer?.data?.title,
    images: queryPrayer?.data?.images,
  });
  const { handleClickNext, handleClickLast, stringKeyLast, stringKeyNext } =
    useAddToQueue({
      type: "prayer",
      id: prayerId,
    });

  return (
    <div css={[quickActionsContainerStyles, cssProp]} {...props}>
      {/* TODO: Add Read quick action and functionality once we get text on screen to work */}

      <QuickAction
        icon={
          queryPrayer.data?.is_favorite ? (
            <StarIcon.Off />
          ) : (
            <StarIcon.Outline />
          )
        }
        onClick={updateFavorite}
        variant={variant}
      >
        {t(
          queryPrayer.data?.is_favorite
            ? "dialog_prayer_options_unfavorite"
            : "dialog_prayer_options_favorite",
        )}
      </QuickAction>

      <HideFromKids>
        <QuickAction
          icon={<ShareIcon />}
          variant={variant}
          onClick={async () =>
            shareModal.show({
              color: queryPrayer.data?.images?.color_hex ?? "#000000",
              imgSrc: queryPrayer.data?.images?.large,
              title: queryPrayer?.data?.title,
              link: await branch.prayer({ id: prayerId }),
            })
          }
        >
          {t("dialog_prayer_options_share")}
        </QuickAction>
      </HideFromKids>

      {queryPrayer?.data?.has_access ? (
        <>
          <QuickAction
            icon={<PlayNextIcon />}
            onClick={handleClickNext}
            variant={variant}
          >
            {t(stringKeyNext)}
          </QuickAction>

          <QuickAction
            icon={<PlayLastIcon />}
            onClick={handleClickLast}
            variant={variant}
          >
            {t(stringKeyLast)}
          </QuickAction>
        </>
      ) : null}
    </div>
  );
};
